import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Input, Button, Card, Icon, Header, Message } from 'semantic-ui-react';
import { useTheme } from './ThemeContext';
import { useToast } from './utils/Toast';
import { useAuth } from './AuthContext';
import { useHealthCheck } from './hooks/useHealthCheck';
import { useApiCache, CacheKeys } from './hooks/useApiCache';
import { supabase } from './supabaseClient';
import { isMockAuthEnabled, clearMockSession, getMockSites } from './utils/mockAuth';
import { handleSessionExpired } from './utils/sessionHandler';
import { fetchSites, SiteRecord, AuthTokenExpiredError, AccessDeniedError } from './api/sites';
import './SiteDashboard.css';

const SiteDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user, role, refreshSession, loading: authLoading } = useAuth();
  // Health check is handled by useHealthCheck hook globally
  useHealthCheck();
  const { getOrFetch } = useApiCache();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [sites, setSites] = useState<SiteRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const nonAdminRedirectRef = useRef(false);

  const logoSrc = theme === 'dark' 
    ? '/assets/Pilbara-risk-solution-logo-whitetext.svg'
    : '/assets/Pilbara-risk-solution-logo-blacktext.svg';

  const loadSites = useCallback(async () => {
    if (authLoading) {
      return;
    }

    if (role !== 'admin') {
      setLoading(false);
      setSites([]);
      return;
    }

    setLoading(true);
    setLoadError(null);

    try {
      // Return mock data if mock auth is enabled
      if (isMockAuthEnabled()) {
        const mockSites = getMockSites() as any;
        setSites(mockSites);
        setLoading(false);
        return;
      }
      
      // Use cache to avoid redundant requests
      const results = await getOrFetch(
        CacheKeys.sites(),
        () => fetchSites(),
        { ttl: 2 * 60 * 1000 } // Cache for 2 minutes
      );
      setSites(results);
    } catch (error) {
      if (error instanceof AuthTokenExpiredError) {
        handleSessionExpired(showToast);
        return;
      }
      if (error instanceof AccessDeniedError) {
        showToast('You do not have permission to view this dashboard.', 'error');
        navigate('/no-access', { replace: true, state: { from: '/dashboard' } });
        return;
      }
      const message = error instanceof Error ? error.message : 'Unable to load sites right now.';
      setLoadError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [authLoading, navigate, role, showToast, getOrFetch]);

  useEffect(() => {
    if (!authLoading) {
      loadSites();
    }
  }, [authLoading, loadSites]);

  useEffect(() => {
    if (authLoading || nonAdminRedirectRef.current) {
      return;
    }

    if (!role) {
      return;
    }

    if (role === 'admin') {
      return;
    }

    nonAdminRedirectRef.current = true;

    if (role === 'admin_client') {
      navigate('/manager-dashboard', { replace: true, state: { from: '/dashboard' } });
      return;
    }

    showToast('You do not have permission to view this dashboard.', 'error');
    navigate('/no-access', { replace: true, state: { from: '/dashboard' } });
  }, [authLoading, navigate, role, showToast]);

  const handleCreateSite = () => {
    navigate('/sites/create');
  };

  const handleSiteClick = (siteId: string) => {
    navigate(`/sites/${siteId}`);
  };

  const handleLogout = async () => {
    try {
      if (isMockAuthEnabled()) {
        clearMockSession();
        await refreshSession();
        showToast('Logged out successfully', 'success');
        navigate('/');
        return;
      }
      
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
      await refreshSession();
      showToast('Logged out successfully', 'success');
      navigate('/');
    } catch (err) {
      console.error('[Dashboard] Failed to sign out', err);
      showToast('Unable to log out right now. Please try again.', 'error');
    }
  };

  const filteredSites = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return sites;
    }

    return sites.filter((site) => {
      const nameMatch = site.siteName?.toLowerCase().includes(query);
      const locationMatch = site.location?.toLowerCase().includes(query);
      return nameMatch || locationMatch;
    });
  }, [sites, searchQuery]);

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <Container>
          <div className="header-content">
            <div className="header-logo">
              <img src={logoSrc} alt="Pilbara Risk Solutions" className="dashboard-logo" />
            </div>
            <div className="header-actions">
              <div className="header-user" title={user?.email ?? undefined}>
                <Icon name='user circle' />
                <span className="user-name">{user?.user_metadata?.full_name?.trim() || user?.email || 'Member'}</span>
              </div>
              <Button 
                icon="users" 
                content="Admin Dashboard" 
                onClick={() => navigate('/dashboard')}
                className="header-button"
              />
              <Button 
                className="logout-button" 
                onClick={handleLogout}
                icon
                labelPosition='right'
              >
                <Icon name='sign out' />
                Logout
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <Container className="dashboard-main">
        <div className="dashboard-title-section">
          <Header as="h1" className="dashboard-title">
            Admin Site Dashboard
          </Header>
              <p className="dashboard-subtitle">Search and oversee every available site</p>
        </div>

        <div className="dashboard-controls">
          <div className="search-container">
            <Input
              icon="search"
              iconPosition="left"
              placeholder="Search all sites..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              size="large"
            />
          </div>
          <Button
            primary
            size="large"
            icon
            labelPosition='left'
            onClick={handleCreateSite}
            className="create-button"
          >
            <Icon name='plus' />
            Create Site
          </Button>
        </div>

        <div className="sites-grid">
          {loading ? (
            <div className="loading-message">Loading sites...</div>
          ) : loadError ? (
            <Message negative>
              <Message.Header>Unable to load sites</Message.Header>
              <p>{loadError}</p>
              <Button basic color="red" onClick={loadSites} className="retry-button">
                <Icon name="refresh" /> Try again
              </Button>
            </Message>
          ) : filteredSites.length === 0 ? (
            <div className="empty-message">
              <Icon name="search" size="huge" className="empty-icon" />
              <p>No sites found</p>
            </div>
          ) : (
            filteredSites.map((site) => {
              const hazardCount = site.hazardEntries?.length ?? 0;
              return (
                <Card
                  key={site.id}
                  className="site-card"
                  onClick={() => handleSiteClick(site.id)}
                >
                  <Card.Content>
                    <Card.Header className="site-card-header">
                      {site.siteName || 'Untitled site'}
                    </Card.Header>
                    <Card.Meta className="site-card-meta">
                      <Icon name="map marker alternate" aria-hidden="true" />
                      <span>{site.location || 'Location not specified'}</span>
                    </Card.Meta>
                    <Card.Description className="site-card-description">
                      <div className="site-info-row">
                        <span className="info-label">Created</span>
                        <span className="info-value">
                          {site.createdAt ? new Date(site.createdAt).toLocaleDateString() : 'Unknown'}
                        </span>
                      </div>
                      <div className="site-info-row">
                        <span className="info-label">Hazards</span>
                        <span className="info-value">{hazardCount}</span>
                      </div>
                    </Card.Description>
                  </Card.Content>
                </Card>
              );
            })
          )}
        </div>

        {filteredSites.length > 0 && !loading && !loadError && (
          <div className="dashboard-footer">
            <p>Showing {filteredSites.length} of {sites.length} sites</p>
          </div>
        )}
      </Container>
    </div>
  );
};

export default SiteDashboard;
